<template>
  <Card class="cardClass">
    <Toolbar>
      <v-toolbar-title class="fs-16">{{ $t('Daily Updates') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <TextInput :searchQuery="search" @search-input-change="searchChangeHandler" :isShowOption="false"></TextInput>
      <AvatarButton icon="cached" :tip="$t('Refresh')" @click="refresh"></AvatarButton>
      <AvatarButton icon="get_app" :tip="$t('Download Excel')" class="hidden-sm-and-down" @click="download">
      </AvatarButton>
    </Toolbar>
    <v-layout column>
      <MobileList :data="tableData" v-if="isMobile"></MobileList>
      <v-data-table class="elevation-1 dataTable" fixed-header :headers="headers" :items="tableData"
        :search="search" hide-actions :loading="dataLoading" :custom-sort="sortByNum"
        :pagination.sync="pagination" must-sort>
        <template v-slot:items="props">
          <tr @click="selectPlant(props.item.plant_id, props.item.plant_name)">
            <td>
              {{ props.item.plant_name }} </td>
            <td>
              {{ getGeneralState('plantData')[props.item.plant_id].city }}</td>
            <td>
              {{ props.item.location }}</td>
            <td style="text-align:center" :class="{'error--text': !props.item.generation}">
              {{ props.item.generation != null ? props.item.generation : $t("No data") }}</td>
            <td style="text-align:center" :class="{'error--text': !props.item.power}">
              {{ props.item.power != null ? props.item.power : $t("No data") }}</td>
            <td style="text-align:center" :class="{'error--text': !props.item.acyield}">
              {{ props.item.acyield != null ? props.item.acyield : $t("No data") }}</td>
            <td xs12 sm12 md12 lg12 xl12>
              <span v-for="value, key in props.item.alert" v-bind:key="key">
                <v-tooltip top>
                  {{value}}
                  <v-btn v-bind:class="value" fab small slot="activator"
                    class="fab-btn-xs alert-indicator"
                    v-on:click="redirectAlert(props.item.plant_id, alertShorthand[key])">
                    {{alertShorthand[key]}}
                  </v-btn>
                  <span>{{key.toUpperCase()}}</span>
                  <br />
                  <span v-if="key == 'communication'">
                    <span>Last Alert: </span>
                    <span style="font-size: 1.2em;"> {{props.item.latestAlerts[key] || '-'}} </span>
                  </span>
                </v-tooltip>
              </span>
            </td>
            <td style="text-align:center">
              {{ props.item.insolation }}</td>
            <td style="text-align:center"  id="ratio">
              <span v-if="props.item.ratio=='N/A'">N/A</span>
              <span v-else-if="props.item.ratio>100">---</span>
              <span v-else>{{props.item.ratio | round}}</span>
            </td>
          </tr>
        </template>
        <v-alert id="search" slot="no-results" :value="true" color="error" icon="warning">
          {{ $t('Your search for') }} "{{ search }}" {{ $t('found no results') }}
        </v-alert>
      </v-data-table>
      <div class="legend-background">
        <v-btn class="legend" xs3 sm3 md3 lg3 xl3 color="error" fab small></v-btn>
        <span>{{ $t('New Alerts') }}</span>
        <v-btn class="legend" xs3 sm3 md3 lg3 xl3 color="success" fab small></v-btn>
        <span>{{ $t('No Alerts') }}</span>
      </div>
    </v-layout>
  </Card>
</template>

<script>
import {
  mapGetters,
  mapMutations,
  mapActions
} from 'vuex'
import moment from 'moment'
import ExcelJS from 'exceljs'
import Home from '@/services/Home'
import TextInput from '@/components/home/TextInput.vue'
import AvatarButton from '@/components/home/AvatarButton.vue'
import MobileList from '@/components/home/DataTable/MobileList.vue'

import {
  mixin,
  mapGettersMixin
} from '@/util/mixin/index'

export default {
  mixins: [ mixin, mapGettersMixin ],
  components: {
    TextInput,
    AvatarButton,
    MobileList
  },
  data() {
    return {
      alertShorthand: {
        inverter: 'I',
        grid: 'G',
        scb: 'S',
        communication: 'C',
        transformer: 'T',
        computed: 'P'
      },
      pagination: {
        rowsPerPage: 15,
        sortBy: "plant_name",
        descending: false
      },
      search: "",
      page: 1,
      tableData: [],
      param: {
        plantList: []
      },
      dataLoading: true,
      alert: {
        G: 'grid',
        I: 'inverter',
        C: 'communication',
        T: 'transformer',
        P: 'PR'
      }
    }
  },
  mounted() {
    this.setIntervalWithoutDelay(this.callAllApi)
  },
  computed: {
    ...mapGetters([
      'getPlantName',
      'getGeneralState',
      'getPlantList',
      'getPlantId'
    ]),
    indicator() {
      return this.alert ? 'error blink' : 'success'
    },
    isMobile() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
        case 'sm':
          return true
        case 'md':
        case 'lg':
        case 'xl':
          return false
      }
    },
    headers() {
      var head = [{
        text: this.$t('Name'),
        align: 'left',
        value: 'plant_name'
      },
      {
        text: this.$t('City'),
        value: 'city'
      },
      {
        text: this.$t('State'),
        value: 'location'
      },
      {
        text: this.$t('Gen (kWh)'),
        value: 'generation',
        align: 'center'
      },
      {
        text: this.$t('AC Power (kW)'),
        value: 'power',
        align: 'center'
      },
      {
        text: this.$t('Yield (kWh/kWp)'),
        value: 'acyield',
        align: 'center'
      },
      {
        text: this.$t('Status'),
        sortable: false,
        value: 'alert',
        width: '180px',
        align: 'center'
      },
      {
        text: this.$t('Insolation'),
        value: 'insolation',
        align: 'center'
      },
      {
        text: this.$t('PR (%)'),
        value: 'ratio',
        align: 'center'
      }
      ]
      return head
    },
    plantListForCurrentPage() {
      return this.getPlantList.map(el => el.id);
    }
  },
  watch: {
    isReload() {
      if (this.isReload) {
        this.setIntervalWithoutDelay(this.callAllApi)
      }
    },
    page() {
      this.getGenerationDetails()
    }
  },
  methods: {
    ...mapActions(['fetchActivePlantDetails']),
    ...mapMutations([
      'SET_SELECTED_COMPONENT_MENU',
      'RESET_STATE',
      'SET_LAST_CALL_TIME',
      'SET_TOTAL_PR',
      'SET_PROGRESS_BAR'
    ]),
    searchChangeHandler(payload) {
      this.search = payload
    },
    sortByNum(item, index, isDesc) {
      function numericCompare(a, b) {
        if (isDesc === true) {
          if (parseFloat(a[index].toString().replace(/,/gi, '')) < parseFloat(b[index].toString().replace(/,/gi, ''))) {
            return -1
          }
          if (parseFloat(a[index].toString().replace(/,/gi, '')) > parseFloat(b[index].toString().replace(/,/gi, ''))) {
            return 1
          }
        } else if (isDesc === false) {
          if (parseFloat(a[index].toString().replace(/,/gi, '')) > parseFloat(b[index].toString().replace(/,/gi, ''))) {
            return -1
          }
          if (parseFloat(a[index].toString().replace(/,/gi, '')) < parseFloat(b[index].toString().replace(/,/gi, ''))) {
            return 1
          }
        }
        return 0
      }

      function stringCompare(a, b) {
        if (isDesc === true) {
          if (a[index] < b[index]) {
            return -1
          }
          if (a[index] > b[index]) {
            return 1
          }
        } else if (isDesc === false) {
          if (a[index] > b[index]) {
            return -1
          }
          if (a[index] < b[index]) {
            return 1
          }
        }
        return 0
      }

      function prSort(a, b) {
        if (isDesc === true) {
          if (typeof a[index] === typeof b[index]) {
            return a[index] < b[index] ? -1 : 1
          }
          return typeof a[index] > typeof b[index] ? -1 : 1
        } else if (isDesc === false) {
          if (typeof a[index] === typeof b[index]) {
            return a[index] > b[index] ? -1 : 1
          }
          return typeof a[index] < typeof b[index] ? -1 : 1
        }
        return 0
      }
      if (index === 'generation') {
        return item.sort(numericCompare)
      } else if (index === 'plant_name') {
        return item.sort(stringCompare)
      } else if (index === 'city') {
        return item.sort(stringCompare)
      } else if (index === 'location') {
        return item.sort(stringCompare)
      } else if (index === 'ratio') {
        return item.sort(prSort)
      } else if (index === 'insolation') {
        return item.sort(numericCompare)
      } else if (index === 'power') {
        return item.sort(numericCompare)
      } else if (index === 'acyield') {
        return item.sort(numericCompare)
      } else {
        return item
      }
    },

    callAllApi() {
      let updateTime = moment().format('MM-DD-YYYY H:mm:ss')
      this.SET_LAST_CALL_TIME(updateTime)
      this.getGenerationDetails()
    },
    async redirectAlert(plantId) {
      await this.fetchActivePlantDetails(plantId)
      this.$router.push({
        'name': 'alert'
      })
    },
    async getGenerationDetails() {
      if (this.plantListForCurrentPage.length === 0) {
        this.tableData.splice(0)
        return
      }

      this.dataLoading = true
      const param = {
        plantList: this.plantListForCurrentPage
      }
      this.SET_PROGRESS_BAR(true)
      try {
        let response = await Home.dashboardData(param)
        this.SET_PROGRESS_BAR(false)
        let res = response.data.message
        this.tableData = []
        let count = 0
        let totalPr = 0
        Object.values(res).forEach(element => {
          let plantDetails = Object.assign({}, element)
          if (element.generation) {
            plantDetails.generation = element.generation
          }
          plantDetails.location = this.getGeneralState('plantData')[element.plant_id].state
          plantDetails.plant_name = this.getPlantName(element.plant_id)

          if (element.ratio && element['ratio'] < 100 && element['ratio'] !== 0 && !isNaN(element['ratio'])) {
            totalPr += parseInt(element['ratio'])
            count++
          }
          this.$set(this.tableData, this.tableData.length, plantDetails)
        })
        this.SET_TOTAL_PR((totalPr / count).toFixed(2))

        this.dataLoading = false
      } catch (err) {
        this.SET_PROGRESS_BAR(false)
      }
    },
    refresh() {
      this.tableData.length = 0
      this.setIntervalWithoutDelay(this.callAllApi)
    },
    async selectPlant(item) {
      await this.fetchActivePlantDetails(item)
      this.SET_SELECTED_COMPONENT_MENU('plantOverview')
      this.$router.push('/plantOverview')
    },
    async download() {
      const dataCopy = JSON.parse(JSON.stringify(this.tableData));
      if (dataCopy.length === 0) return;

      if (this.pagination.sortBy) {
        this.applySort(
          dataCopy,
          this.pagination.sortBy,
          this.pagination.descending
        );
      }

      this.SET_PROGRESS_BAR(true);
      const headers = this.headers;

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Plant Report");
      worksheet.views = [{ showGridLines: false }];

      try {
        const logoAsset = require("../../../assets/logo/enerman-etisol_new.png");
        const response = await fetch(logoAsset);
        const arrayBuffer = await response.arrayBuffer();
        const imageId = workbook.addImage({
          buffer: arrayBuffer,
          extension: "png",
        });
        worksheet.addImage(imageId, {
          tl: { col: headers.length - 1, row: 0 },
          ext: { width: 150, height: 45 },
        });
      } catch (e) {
        console.error("Could not load logo for Excel", e);
      }

      const titleRow = worksheet.getRow(1);
      titleRow.values = ["ETi-SOL - Plant Report"];
      titleRow.getCell(1).font = { size: 16, bold: true };

      worksheet.addRow(["Date:", moment().format("DD-MM-YYYY")]);
      worksheet.addRow(["System Time:", moment().format("hh:mm A")]);
      worksheet.addRow([]);

      const headerRow = worksheet.addRow(headers.map((h) => h.text));
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF000427" },
        };
        cell.font = {
          color: { argb: "FFFFFFFF" },
          bold: true,
        };
        cell.alignment = { horizontal: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      dataCopy.forEach((item) => {
        const rowValues = headers.map((h) => {
          let val = item[h.value];

          if (h.value === "alert") {
            if (item.alert) {
              const active = [];
              if (item.alert.grid === "alert") active.push("Grid");
              if (item.alert.communication === "alert") active.push("Comm");
              if (item.alert.inverter === "alert") active.push("Inverter");
              if (item.alert.scb === "alert") active.push("SCB");
              val = active.join(", ") || "No Alert";
            } else {
              val = "NA";
            }
          } else if (h.value === "ratio") {
            val =
              item.ratio >= 100
                ? "--"
                : item.ratio === "N/A"
                ? "No data"
                : item.ratio;
          } else if (h.value === "city") {
             val = this.getGeneralState('plantData')[item.plant_id].city;
          }
          return val !== undefined && val !== null ? val : "";
        });

        const row = worksheet.addRow(rowValues);

        // Highlight Plant Name bold and border
        const plantNameCell = row.getCell(1);
        plantNameCell.font = { bold: true };

        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      worksheet.columns = headers.map((h) => ({
        width: (parseInt(h.width) || 150) / 7.5,
      }));

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `Plant_Report_${moment().format(
        "YYYY-MM-DD_HHmm"
      )}.xlsx`;
      anchor.click();
      window.URL.revokeObjectURL(url);
      this.SET_PROGRESS_BAR(false);
    },
    applySort(items, index, isDesc) {
      const numericCompare = (a, b) => {
        if (isDesc === true) {
          if (
            parseFloat((a[index] || 0).toString().replace(/,/gi, "")) <
            parseFloat((b[index] || 0).toString().replace(/,/gi, ""))
          ) {
            return -1;
          }
          if (
            parseFloat((a[index] || 0).toString().replace(/,/gi, "")) >
            parseFloat((b[index] || 0).toString().replace(/,/gi, ""))
          ) {
            return 1;
          }
        } else if (isDesc === false) {
          if (
            parseFloat((a[index] || 0).toString().replace(/,/gi, "")) >
            parseFloat((b[index] || 0).toString().replace(/,/gi, ""))
          ) {
            return -1;
          }
          if (
            parseFloat((a[index] || 0).toString().replace(/,/gi, "")) <
            parseFloat((b[index] || 0).toString().replace(/,/gi, ""))
          ) {
            return 1;
          }
        }
        return 0;
      };

      const stringCompare = (a, b) => {
        if (isDesc === true) {
          if (a[index] < b[index]) {
            return -1;
          }
          if (a[index] > b[index]) {
            return 1;
          }
        } else if (isDesc === false) {
          if (a[index] > b[index]) {
            return -1;
          }
          if (a[index] < b[index]) {
            return 1;
          }
        }
        return 0;
      };

      const prSort = (a, b) => {
        if (isDesc === true) {
          if (typeof a[index] === typeof b[index]) {
            return a[index] < b[index] ? -1 : 1;
          }
          return typeof a[index] > typeof b[index] ? -1 : 1;
        } else if (isDesc === false) {
          if (typeof a[index] === typeof b[index]) {
            return a[index] > b[index] ? -1 : 1;
          }
          return typeof a[index] < typeof b[index] ? -1 : 1;
        }
        return 0;
      };

      if (
        [
          "generation",
          "power",
          "acyield",
          "insolation",
        ].includes(index)
      ) {
        items.sort(numericCompare);
      } else if (index === "ratio") {
        items.sort(prSort);
      } else {
        items.sort(stringCompare);
      }
    }
  }
}
</script>

<style scoped>
.dataTable {
  height: calc(100vh - 305px);
  overflow: auto;
  display: flex;
}

.no_alert {
  background-color: #61B865 !important;
  color: white;
}

.alert-indicator {
  height: 22px;
  width: 22px
}

.alert {
  animation: blinker 1s linear infinite;
  background-color: #E14C50 !important;
  color: white !important;
}

input {
  outline: none;
  margin: 6px;
}

input {
  -webkit-appearance: textfield;
  box-sizing: content-box;
  -webkit-box-sizing: content-box;
  font-family: inherit;
  font-size: 14px;
}

input::-webkit-search-decoration,
input::-webkit-search-cancel-button {
  display: none;
}

input {
  padding: 5px 20px 5px 10px;
  width: 70px;

  -webkit-border-radius: 10em;
  -moz-border-radius: 10em;
  border-radius: 10em;

  -webkit-transition: all 0.4s;
  -moz-transition: all 0.4s;
  transition: all 0.4s;
}

input:focus {
  width: 130px;
  /* -webkit-box-shadow: 0 0 5px rgba(109, 207, 246, 0.5);
    -moz-box-shadow: 0 0 5px rgba(109, 207, 246, 0.5);
    box-shadow: 0 0 5px rgba(109, 207, 246, 0.5); */
}

.legend {
  height: 12px !important;
  width: 12px !important;
  position: relative;
}

.right-space {
  margin-right: 10px;
}

.blink {
  animation: blinker 1s linear infinite;
  /* background-color: red !important; */
  /* color: white !important; */
}

ack,
.ack {
  background-color: yellow !important;
  color: black !important;
}

.fab-btn-xs {
  height: 20px;
  width: 20px;
}

/* Theme */

.theme--dark input {
  background: url(../../../assets/baseline-search-light.svg) no-repeat right center;
}

.theme--light input {
  background: url(../../../assets/baseline-search-light.svg) no-repeat right center;
  border: solid 1px;
}

.avatar-bg {
  background-color: transparent;
  border-color: var(--v-primary-base);
  border-width: 2px;
  border-style: solid;
}

.theme--light .avatar-bg {
  /* border-color: lightgray; */
  border-width: 1px;
  border-style: solid;
}

</style>

<style>
.dataTable th {
  z-index: 1;
  position: sticky;
  top: var(--v-extraGrey) !important;
  background: var(--v-primary-base) !important;
}

.dataTable .v-table__overflow {
  overflow: auto
}

.con-tooltip {
  /* #b27221 */
  /* background: #E9952A; */
  background: #cc535673;
  cursor: pointer;
}

.renew-msg {
  margin-bottom: 6px;
  margin-top: 6px;
}

/*tooltip */
.tooltip {
  visibility: hidden;
  z-index: 1;
  opacity: 0.70;

  width: 20%;
  padding: 0px 20px;
  border: 2px solid #cc535673;
  background: #cc535673;
  color: white;

  position: absolute;
  top: -149%;
  left: 15%;

  border-radius: 9px;
  font: 16px;

  transform: translateY(9px);
  transition: all 0.3s ease-in-out;

  box-shadow: 0 0 3px rgba(56, 54, 54, 0.86);
}

/* tooltip  after*/
.tooltip::after {
  content: " ";
  width: 0;
  height: 0;

  border-style: solid;
  border-width: 12px 12.5px 0 12.5px;
  border-color: #cc5356 transparent transparent transparent;

  position: absolute;
  left: 40%;

}

.con-tooltip:hover .tooltip {
  visibility: visible;
  transform: translateY(-10px);
  /* opacity: 1; */
  transition: .3s linear;
  animation: odsoky 1s ease-in-out infinite alternate;
}

@keyframes odsoky {
  0% {
    transform: translateY(6px);
  }

  100% {
    transform: translateY(1px);
  }

}

/*hover ToolTip*/
.top:hover {
  transform: translateY(0px);
}

.legend-background {
  /* background-color: lightgray; */
  padding: 4px;
  margin-top: 17px;
}

.legend-background span {
  /* color: black; */
  font-size: 14px;
}
</style>
